"use client";

import { useCallback, useEffect, useState } from "react";
import type { TelegramLoginUser } from "./types";

/* The Telegram Login Widget, driven from our own button.

   The whitelabel embeds Telegram's iframe button for sign-in and calls
   `Telegram.Login.auth` for linking. Here both go through `Telegram.Login.auth`,
   so the dialog keeps one button style: the script opens Telegram's own popup
   on oauth.telegram.org, the player confirms there, and the callback receives
   the signed user object (or `false` when they close it).

   Two things must hold for the popup to open:

   - The script must already be loaded when the button is clicked. The popup
     is opened by the click itself; a click that first awaits a script load
     is no longer a user gesture and the browser blocks the window. So the
     script is loaded as soon as the dialog shows a Telegram button, and the
     button waits for it.
   - The bot's domain (BotFather `/setdomain`) must be this site's host, or
     Telegram answers "Bot domain invalid" inside the popup. */

const WIDGET_SCRIPT = "https://telegram.org/js/telegram-widget.js?22";

let loading: Promise<void> | null = null;

const loadWidget = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("No window."));
  }

  if (window.Telegram?.Login) {
    return Promise.resolve();
  }

  loading ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = WIDGET_SCRIPT;
    script.async = true;
    script.addEventListener("load", () => (window.Telegram?.Login ? resolve() : reject(new Error("No widget."))));
    script.addEventListener("error", () => reject(new Error("The Telegram script did not load.")));
    document.body.appendChild(script);
  }).catch((error: unknown) => {
    /* A blocked script (an ad blocker, mostly) may be unblocked and retried. */
    loading = null;
    throw error;
  });

  return loading;
};

export type TelegramLogin = {
  ready: boolean;
  /* The script did not load — nearly always an ad or tracker blocker. */
  failed: boolean;
  /* Opens Telegram's popup. Resolves with the user, or null when the player
     closed it without confirming. */
  authenticate: (botId: number) => Promise<TelegramLoginUser | null>;
};

export const useTelegramLogin = (enabled: boolean): TelegramLogin => {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    loadWidget().then(
      () => {
        if (!cancelled) {
          setReady(true);
          setFailed(false);
        }
      },
      () => {
        if (!cancelled) setFailed(true);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const authenticate = useCallback(
    (botId: number) =>
      new Promise<TelegramLoginUser | null>((resolve) => {
        const login = window.Telegram?.Login;

        if (!login) {
          resolve(null);
          return;
        }

        /* No `request_access`: the bot is not asking to message the player,
           only to vouch for who they are — the whitelabel asks the same. */
        login.auth({ bot_id: botId }, (user) => resolve(user === false ? null : user));
      }),
    [],
  );

  return { ready, failed, authenticate };
};
